import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import CheckInsTab from "@/components/CheckInsTab";
import PhotosTab from "@/components/PhotosTab";
import { PlanTab, ProtocolTab } from "@/components/PlanTabs";
import StrengthTab from "@/components/StrengthTab";
import MessagesTab from "@/components/MessagesTab";
import MealGridTab from "@/components/MealGridTab";
import WorkoutGridTab from "@/components/WorkoutGridTab";
import BillingTab from "@/components/BillingTab";
import VideoReviewsTab from "@/components/VideoReviewsTab";

const TABS = [
  { v: "checkins",     label: "Check-ins" },
  { v: "messages",     label: "Messages" },
  { v: "photos",       label: "Photos" },
  { v: "meal",         label: "Meal Plan" },
  { v: "workout",      label: "Workout" },
  { v: "strength",     label: "Strength" },
  { v: "cardio",       label: "Cardio" },
  { v: "ped",          label: "PED Protocol" },
  { v: "supplement",   label: "Supplements" },
  { v: "bloodwork",    label: "Bloodwork" },
  { v: "video_review", label: "Video Reviews" },
  { v: "billing",      label: "Billing" },
];

export default function ClientView() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [client, setClient] = useState(null);
  const [err, setErr] = useState(null);
  const [unreadMsgs, setUnreadMsgs] = useState(0);

  const initialTab = useMemo(() => searchParams.get("tab") || "checkins", [searchParams]);
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/clients/${id}`);
        if (!cancel) setClient(data);
      } catch (e) {
        if (!cancel) setErr(e.response?.status === 403 ? "Access denied" : "Not found");
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  // Load unread message count for badge
  const loadUnread = async () => {
    try {
      const { data } = await api.get(`/clients/${id}/messages`);
      const field = user?.role === "trainer" ? "read_by_trainer" : "read_by_client";
      const otherRole = user?.role === "trainer" ? "client" : "trainer";
      setUnreadMsgs(data.filter(m => m.sender_role === otherRole && !m[field]).length);
    } catch {}
  };
  useEffect(() => {
    if (!client) return;
    loadUnread();
    const id2 = setInterval(loadUnread, 15000);
    return () => clearInterval(id2);
    // eslint-disable-next-line
  }, [client?.id]);

  const onTabChange = (v) => {
    setTab(v);
    setSearchParams((sp) => { sp.set("tab", v); return sp; }, { replace: true });
  };

  if (err) return <AppShell title="Error"><div className="text-red-400">{err}</div></AppShell>;
  if (!client || !user) return <AppShell title="Loading..."><div className="text-zinc-500 text-xs uppercase tracking-[0.3em]">Loading client...</div></AppShell>;

  const isSelf = user.role === "client" && user.id === client.id;
  const title = isSelf ? "My Plan" : client.name;

  return (
    <AppShell
      title={title}
      actions={user.role === "trainer" ? (
        <Button onClick={() => navigate("/app")} variant="outline" className="rounded-none border-zinc-800 bg-transparent uppercase tracking-[0.2em] h-10 text-xs hover:bg-zinc-900 hover:text-white" data-testid="back-to-clients">
          ← All Clients
        </Button>
      ) : null}
    >
      <div className="mb-8 flex flex-wrap items-center gap-6 border-y border-zinc-800 py-5">
        <Meta label="Client" value={client.name} />
        <Meta label="Email" value={client.email} />
        {client.created_at && <Meta label="Since" value={new Date(client.created_at).toLocaleDateString()} />}
      </div>

      <Tabs value={tab} onValueChange={onTabChange}>
        <TabsList className="rounded-none bg-transparent border-b border-zinc-800 w-full justify-start gap-2 md:gap-6 p-0 h-auto flex-wrap">
          {TABS.map(t => (
            <TabsTrigger key={t.v} value={t.v} data-testid={`tab-${t.v}`} className="relative rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF3B30] data-[state=active]:text-white data-[state=active]:bg-transparent data-[state=active]:shadow-none text-zinc-500 uppercase tracking-[0.2em] text-[11px] h-11 px-1 md:px-2">
              {t.label}
              {t.v === "messages" && unreadMsgs > 0 && (
                <span data-testid="messages-unread-badge" className="ml-2 inline-flex items-center justify-center bg-[#FF3B30] text-white text-[9px] font-mono min-w-[16px] h-4 px-1">
                  {unreadMsgs > 9 ? "9+" : unreadMsgs}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <div className="mt-10">
          <TabsContent value="checkins"><CheckInsTab clientId={client.id} user={user} /></TabsContent>
          <TabsContent value="messages"><MessagesTab clientId={client.id} user={user} onUnreadChange={loadUnread} /></TabsContent>
          <TabsContent value="photos"><PhotosTab clientId={client.id} /></TabsContent>
          <TabsContent value="meal"><MealGridTab clientId={client.id} user={user} /></TabsContent>
          <TabsContent value="workout"><WorkoutGridTab clientId={client.id} user={user} /></TabsContent>
          <TabsContent value="strength"><StrengthTab clientId={client.id} user={user} /></TabsContent>
          <TabsContent value="cardio"><ProtocolTab clientId={client.id} user={user} ptype="cardio" label="Cardio Regimen" /></TabsContent>
          <TabsContent value="ped"><ProtocolTab clientId={client.id} user={user} ptype="ped" label="PED Protocol" /></TabsContent>
          <TabsContent value="supplement"><ProtocolTab clientId={client.id} user={user} ptype="supplement" label="Supplement Stack" /></TabsContent>
          <TabsContent value="bloodwork"><ProtocolTab clientId={client.id} user={user} ptype="bloodwork" label="Bloodwork Review" /></TabsContent>
          <TabsContent value="video_review"><VideoReviewsTab clientId={client.id} user={user} /></TabsContent>
          <TabsContent value="billing"><BillingTab clientId={client.id} user={user} /></TabsContent>
        </div>
      </Tabs>
    </AppShell>
  );
}

const Meta = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{label}</div>
    <div className="font-mono text-sm text-zinc-200 mt-1">{value}</div>
  </div>
);
